import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@hyperhedral/ui";
import { supabase } from "../lib/supabase";
import { invitationToken, clearInvitation } from "../lib/invitation";
import { Downloads } from "./portal/Downloads";

// Share a single verification during React StrictMode's mount replay.
let verification: { token: string; promise: ReturnType<typeof supabase.auth.verifyOtp> } | undefined;
export function Welcome() {
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [licenseKey, setLicenseKey] = useState("");

  useEffect(() => {
    let active = true;
    async function accept() {
      try {
        const token = invitationToken();
        if (token) {
          if (verification?.token !== token) verification = { token, promise: supabase.auth.verifyOtp({ type: "invite", token_hash: token }) };
          const result = await verification.promise;
          if (result.error) {
            clearInvitation();
            throw new Error("This invitation has expired or has already been used. Sign in if you accepted it before, or ask us for a new invitation.");
          }
          clearInvitation();
        }
        const { data, error: authError } = await supabase.auth.getUser();
        if (authError || !data.user?.email_confirmed_at) throw new Error("Open the personal link in your invitation email to get started, or sign in to resume setup.");
        const { data: setup, error: stateError } = await supabase.rpc("invitation_status");
        if (stateError) throw new Error("We couldn't load your setup. Please refresh and try again.");
        if (!setup) throw new Error("Your account already uses the regular signup flow. Continue to your account to find your licence and downloads.");
        if (setup.completed) {
          const { data: license, error: licenseError } = await supabase.from("licenses").select("license_key").eq("id", setup.license_id).single();
          if (licenseError) throw new Error("We couldn't load your licence. Please refresh and try again.");
          if (active) setLicenseKey(license.license_key);
          return;
        }
        if (active) {
          setEmail(data.user.email ?? "");
          setFullName(data.user.user_metadata.full_name ?? "");
          setSchoolName(data.user.user_metadata.school_name ?? "");
          setReady(true);
        }
      } catch (err) { if (active) setError(err instanceof Error ? err.message : "We couldn't open this invitation. Please try again."); }
    }
    void accept();
    return () => { active = false; };
  }, []);

  async function finish(event: React.FormEvent) {
    event.preventDefault();
    if (!accepted || busy) return;
    setBusy(true); setError("");
    try {
      const { error: passwordError } = await supabase.auth.updateUser({ password, data: { full_name: fullName.trim(), school_name: schoolName.trim() } });
      // A previous attempt may have saved the password before a network failure.
      if (passwordError && passwordError.code !== "same_password") throw passwordError;
      const { data: licenseId, error: setupError } = await supabase.rpc("complete_invitation", { p_full_name: fullName.trim(), p_school_name: schoolName.trim(), p_accept_terms: accepted });
      if (setupError) throw setupError;
      const { data: license, error: licenseError } = await supabase.from("licenses").select("license_key").eq("id", licenseId).single();
      if (licenseError) throw licenseError;
      setPassword(""); setLicenseKey(license.license_key);
    } catch (err) { setError(err instanceof Error ? err.message : "Setup couldn't be completed. Please retry; your trial will only be created once."); }
    finally { setBusy(false); }
  }

  if (licenseKey) return <main className="mx-auto max-w-4xl px-6 py-12">
    <h1 className="font-display text-3xl text-forest-950">You're ready to start.</h1>
    <p className="mt-3 text-bark-600">Download Bookburrow, then enter this licence key in the app.</p>
    <div className="my-6 rounded-xl border border-forest-200 bg-forest-50 p-5">
      <p className="text-sm text-forest-700">Your licence key</p>
      <code className="mt-2 block break-all text-lg text-forest-950">{licenseKey}</code>
    </div>
    <Downloads />
    <Link className="text-forest-700 underline" to="/portal/dashboard">Go to your account</Link>
  </main>;

  return <main className="account-page px-6 py-12">
    <div className="account-card mx-auto max-w-lg">
      <p className="editorial-label text-forest-700">Just one quick step</p>
      <h1 className="mt-3 font-display text-3xl text-forest-950">Welcome to Bookburrow.</h1>
      <p className="mt-3 text-bark-600">Finish setup to start your 30-day free trial. No credit card required.</p>
      {error && <p role="alert" className="mt-5 rounded-lg bg-red-50 p-4 text-sm text-red-800">{error}</p>}
      {error && <Link className="mt-3 block text-sm underline" to="/portal/dashboard">Go to your account</Link>}
      {!ready ? <p className="mt-5">{error ? <Link className="underline" to="/portal/login?next=welcome">Sign in to your account</Link> : "Checking your invitation…"}</p> : <form onSubmit={finish} className="mt-6 space-y-5">
        <p className="text-sm text-forest-700">Email confirmed: <strong>{email}</strong></p>
        <Input label="Your name" autoComplete="name" value={fullName} onChange={e => setFullName(e.target.value)} required maxLength={200} />
        <Input label="School or library name" autoComplete="organization" value={schoolName} onChange={e => setSchoolName(e.target.value)} required maxLength={200} />
        <Input label="Choose a password" type="password" autoComplete="new-password" minLength={8} value={password} onChange={e => setPassword(e.target.value)} required />
        <p className="text-xs text-bark-600">At least 8 characters.</p>
        <label className="flex items-start gap-3 text-sm text-bark-700"><input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} required className="mt-1" /><span>I agree to the <Link to="/terms" target="_blank" className="underline">Terms of Service</Link> and have read the <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link>.</span></label>
        <button disabled={busy || !accepted} className="w-full rounded-lg bg-forest-800 px-5 py-3 font-semibold text-white disabled:opacity-50">{busy ? "Setting up your account…" : "Start trial and download"}</button>
      </form>}
    </div>
  </main>;
}
